/**
 * lib/recurringVouchers.ts
 * ------------------------------------------------------------------
 * THE BILLS THAT ARRIVE EVERY MONTH.
 *
 * Security, rent, cleaning, insurance, the internet line. Same payee,
 * same amount, every period. Retyping them is where the forgotten
 * payment comes from, and a forgotten payment is a supplier ringing the
 * school about an invoice it has had for six weeks.
 *
 * A TEMPLATE NEVER PAYS ANYTHING. Generating produces a DRAFT voucher,
 * which then walks prepare → check → approve like every other. The whole
 * control of a voucher is three distinct people signing it; a schedule
 * that could approve its own payments would remove that quietly and
 * completely. This is a typing aid with a memory, and nothing more.
 *
 * IT CATCHES UP RATHER THAN SKIPPING. A template left ungenerated for
 * three months offers all three, each dated to its own period. Generating
 * only the current one silently loses the two the school still owes —
 * and those are exactly the invoices nobody remembers.
 *
 * IDEMPOTENT BY PERIOD. A template generates once per period, ever.
 * Opening the screen twice must not raise the rent twice, and a
 * deterministic source id is what makes re-running safe rather than
 * something to be careful about.
 *
 * PURE. Templates and what already exists in, what is due out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { VoucherLine, PaymentMethodOut } from '@/lib/vouchers';

export const RECURRING_VOUCHER_COLLECTION = 'financeRecurringVouchers';

export interface RecurringVoucher {
  id: string;
  name: string;
  payeeName: string;
  supplierId?: string;
  lines: VoucherLine[];
  total: number;
  narration: string;
  paymentRef: string;
  method: PaymentMethodOut;
  /** '2026-01'. The first period this is due for. */
  startPeriod: string;
  /** Absent means it runs until somebody stops it. */
  endPeriod?: string;
  /** Which day of the month it is normally due. */
  dueDay?: number;
  active: boolean;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

/** '2026-01' + 2 → '2026-03'. */
export function addMonths(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number);
  const total = (y * 12) + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

export function monthsBetween(a: string, b: string): number {
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  return (by - ay) * 12 + (bm - am);
}

/** Deterministic, so generating twice for one period cannot double-bill. */
export const recurringVoucherSourceId = (templateId: string, period: string): string =>
  `rv_${templateId}_${period}`;

export function recurringVoucherProblem(
  t: Pick<RecurringVoucher, 'name' | 'payeeName' | 'lines' | 'startPeriod' | 'endPeriod'>,
): string | null {
  if (!t.name?.trim()) return 'Give the standing bill a name.';
  if (!t.payeeName?.trim()) return 'Name who is being paid.';
  if (!t.lines?.length) return 'A standing bill needs at least one line.';
  if (t.lines.some((l) => !l.accountCode)) {
    return 'Code every line — an uncoded standing bill posts to nothing twelve times a year.';
  }
  if (!(round2(t.lines.reduce((n, l) => n + (Number(l.amount) || 0), 0)) > 0)) {
    return 'The total has to be more than nil.';
  }
  if (!/^\d{4}-\d{2}$/.test(t.startPeriod ?? '')) return 'Say which month it starts.';
  if (t.endPeriod && monthsBetween(t.startPeriod, t.endPeriod) < 0) {
    return 'It cannot end before it starts.';
  }
  return null;
}

export interface DueVoucher {
  template: RecurringVoucher;
  period: string;
  /** The date the generated voucher carries — its own period, not today. */
  date: string;
  sourceId: string;
  total: number;
}

/**
 * What each template still owes, oldest period first.
 *
 * `alreadyGenerated` is the set of source ids that exist, so a template
 * generated for March offers April next rather than March again.
 */
export function recurringVouchersDue(
  templates: RecurringVoucher[],
  alreadyGenerated: ReadonlySet<string>,
  upToPeriod: string,
): DueVoucher[] {
  const out: DueVoucher[] = [];

  for (const t of templates) {
    if (!t.active) continue;
    if (recurringVoucherProblem(t)) continue;

    const last = t.endPeriod && monthsBetween(t.endPeriod, upToPeriod) > 0
      ? t.endPeriod
      : upToPeriod;

    const span = monthsBetween(t.startPeriod, last);
    if (span < 0) continue;

    for (let i = 0; i <= span; i += 1) {
      const period = addMonths(t.startPeriod, i);
      const sourceId = recurringVoucherSourceId(t.id, period);
      if (alreadyGenerated.has(sourceId)) continue;

      /* Dated in its OWN period, never today. A March bill generated in
         June belongs to March — dating it today would put three months
         of cost in one month and leave two empty. */
      const day = Math.min(Math.max(1, t.dueDay ?? 1), 28);
      out.push({
        template: t,
        period,
        date: `${period}-${String(day).padStart(2, '0')}`,
        sourceId,
        total: round2(t.lines.reduce((n, l) => n + (Number(l.amount) || 0), 0)),
      });
    }
  }

  return out.sort((a, b) => a.period.localeCompare(b.period)
    || a.template.name.localeCompare(b.template.name));
}

export interface RecurringSummary {
  active: number;
  /** Per month, across every live template. */
  monthlyCommitment: number;
  due: number;
  dueValue: number;
  /** How far behind the furthest template is, in months. */
  worstBacklog: number;
}

export function summariseRecurring(
  templates: RecurringVoucher[],
  due: DueVoucher[],
  currentPeriod: string,
): RecurringSummary {
  const live = templates.filter((t) => t.active);
  const backlog = due.length
    ? Math.max(...due.map((d) => monthsBetween(d.period, currentPeriod)))
    : 0;

  return {
    active: live.length,
    monthlyCommitment: round2(live.reduce((n, t) => n
      + round2((t.lines ?? []).reduce((m, l) => m + (Number(l.amount) || 0), 0)), 0)),
    due: due.length,
    dueValue: round2(due.reduce((n, d) => n + d.total, 0)),
    worstBacklog: Math.max(0, backlog),
  };
}
